import React, { useCallback, useEffect, useState } from 'react';
import type { Collection } from '../domain/collections';
import { isDomainError } from '../domain/errors';
import { useRepositories } from '../hooks/useRepositories';
import { ActionError, ErrorState } from './PageStates';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Delete a collection and everything in it, after its owner has seen how
 * many listings that is (016 FR-022, FR-023). The count comes from a fresh
 * read; if it changed before the delete, the server refuses and the new
 * count is shown to confirm again.
 */
export default function DeleteCollectionDialog({
  collectionId,
  open,
  onOpenChange,
  onDeleted,
}: {
  collectionId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeleted: () => void;
}) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="rounded-3xl border-none bg-background">
        {open && <DeleteBody collectionId={collectionId} onDeleted={onDeleted} />}
      </AlertDialogContent>
    </AlertDialog>
  );
}

type Load =
  | { state: 'loading' }
  | { state: 'failed'; error: unknown }
  | { state: 'ready'; collection: Collection };

function DeleteBody({ collectionId, onDeleted }: { collectionId: string; onDeleted: () => void }) {
  const { collections } = useRepositories();
  const [load, setLoad] = useState<Load>({ state: 'loading' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchCollection = useCallback(async () => {
    try {
      setLoad({ state: 'ready', collection: await collections.get(collectionId) });
    } catch (e) {
      setLoad({ state: 'failed', error: e });
    }
  }, [collections, collectionId]);

  useEffect(() => {
    fetchCollection();
  }, [fetchCollection]);

  const confirm = async () => {
    if (load.state !== 'ready') return;
    setBusy(true);
    setError(null);
    try {
      await collections.delete(collectionId, load.collection.listingCount);
      onDeleted();
    } catch (e) {
      if (isDomainError(e, 'countChanged')) await fetchCollection();
      else setError(e);
    } finally {
      setBusy(false);
    }
  };

  const name = load.state === 'ready' ? load.collection.name : 'this collection';
  const count = load.state === 'ready' ? load.collection.listingCount : 0;

  return (
    <>
      <AlertDialogHeader>
        <AlertDialogTitle>Delete {name}?</AlertDialogTitle>
        <AlertDialogDescription asChild>
          <div>
            {load.state === 'loading' ? (
              <Skeleton role="status" aria-label="Counting listings" className="h-5 w-3/4" />
            ) : load.state === 'failed' ? null : (
              <p>
                {name} holds {count} {count === 1 ? 'listing' : 'listings'}, which will be deleted
                with it. This can't be undone.
              </p>
            )}
          </div>
        </AlertDialogDescription>
      </AlertDialogHeader>
      {load.state === 'failed' && (
        <ErrorState message="Couldn't count the listings." onRetry={fetchCollection} />
      )}
      {error !== null && <ActionError error={error} />}
      <AlertDialogFooter>
        <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
        <Button
          variant="destructive"
          className="rounded-full"
          disabled={busy || load.state !== 'ready'}
          onClick={confirm}
        >
          Delete
        </Button>
      </AlertDialogFooter>
    </>
  );
}
